<script setup>
import { computed } from "vue";
import { useStore } from "vuex";

const store = useStore();
const canManagePurchaseReturns = computed(() => store.getters.hasPermission('purchase.return'));
</script>

<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content">
        <pageaddheader
          :title="title"
          :title1="title1"
          :items="canManagePurchaseReturns ? items : []"
        />

        <div class="card">
          <div class="card-body">
            <div class="table-top">
              <div class="search-input">
                <a class="btn btn-searchset">
                  <img src="../../../assets/img/icons/search-white.svg" alt="search" />
                </a>
                <div class="dataTables_filter">
                  <label>
                    <input
                      v-model="search"
                      type="search"
                      class="form-control form-control-sm"
                      placeholder="Search by return bill, supplier, reason..."
                      @input="searching"
                    />
                  </label>
                </div>
              </div>

              <div class="list-stats">
                <span class="stat-chip">Returns: <strong>{{ pagination.total }}</strong></span>
                <span class="stat-chip">Showing: <strong>{{ data.length }}</strong></span>
              </div>
            </div>

            <div class="filter-row">
              <div class="filter-group">
                <label class="filter-label">Status</label>
                <select v-model="statusFilter" class="form-control form-control-sm filter-input" @change="applyFilters">
                  <option value="">All</option>
                  <option value="DRAFT">Draft</option>
                  <option value="CONFIRMED">Confirmed</option>
                  <option value="CANCELLED">Cancelled</option>
                </select>
              </div>
              <div class="filter-group">
                <label class="filter-label">From</label>
                <input v-model="filterFrom" type="date" class="form-control form-control-sm filter-input" @change="applyFilters" />
              </div>
              <div class="filter-group">
                <label class="filter-label">To</label>
                <input v-model="filterTo" type="date" class="form-control form-control-sm filter-input" @change="applyFilters" />
              </div>
              <button class="btn-clear-filter" @click="clearFilters">Clear</button>
            </div>

            <div v-if="loading" class="loading-wrap">
              <div class="loading-spinner"></div>
              <span>Loading purchase returns...</span>
            </div>

            <div v-else-if="!data.length" class="empty-wrap">
              <p class="empty-title">No purchase returns found</p>
              <p class="empty-sub">Create a draft purchase return to start tracking reversals.</p>
            </div>

            <div v-else class="table-responsive">
              <a-table
                :columns="columns"
                :data-source="data"
                :loading="loading"
                :pagination="paginationConfig"
                :expanded-row-keys="expandedRowKeys"
                row-key="returnid"
                @change="handleTableChange"
                @expand="handleExpand"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'billno'">
                    <div class="bill-cell">
                      <span class="bill-badge">{{ record.billno }}</span>
                      <small class="bill-id">{{ record.returnid }}</small>
                    </div>
                  </template>

                  <template v-else-if="column.key === 'status'">
                    <span class="status-pill" :class="statusClass(record.status)">{{ record.status }}</span>
                  </template>

                  <template v-else-if="column.key === 'netamount'">
                    <span class="price-total">Rs. {{ formatNumber(record.netamount) }}</span>
                  </template>

                  <template v-else-if="column.key === 'returndate'">
                    <span class="date-text">{{ formatDate(record.returndate) }}</span>
                  </template>

                  <template v-else-if="column.key === 'action'">
                    <div class="action-btns">
                      <button class="act-btn act-open" @click="openReturn(record.returnid)">Open</button>
                      <button class="act-btn act-print" @click="printReturn(record)" title="Print">🖨️</button>
                      <button
                        v-if="canManagePurchaseReturns && record.status === 'DRAFT'"
                        class="act-btn act-confirm"
                        @click="confirmReturn(record.returnid)"
                      >
                        Confirm
                      </button>
                      <button
                        v-if="canManagePurchaseReturns && ['DRAFT', 'CONFIRMED'].includes(record.status)"
                        class="act-btn act-cancel"
                        @click="cancelReturn(record.returnid)"
                      >
                        Cancel
                      </button>
                    </div>
                  </template>
                </template>

                <template #expandedRowRender="{ record }">
                  <div class="expanded-wrap">
                    <div v-if="detailLoading[record.returnid]" class="expanded-loading">Loading return items...</div>
                    <div v-else-if="returnDetails[record.returnid]">
                      <div class="detail-header">
                        <span>Supplier: <strong>{{ returnDetails[record.returnid].vendorname || record.vendor || "-" }}</strong></span>
                        <span>Reason: <strong>{{ returnDetails[record.returnid].reason || "-" }}</strong></span>
                        <span>Remarks: <strong>{{ returnDetails[record.returnid].remarks || "-" }}</strong></span>
                      </div>
                      <table class="detail-table">
                        <thead>
                          <tr>
                            <th>Purchase Bill</th>
                            <th>Item</th>
                            <th>Return Qty</th>
                            <th>Price</th>
                            <th>Tax Type</th>
                            <th>Tax %</th>
                            <th>Taxable</th>
                            <th>CGST</th>
                            <th>SGST</th>
                            <th>IGST</th>
                            <th>Tax</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="item in returnDetails[record.returnid].items || []" :key="item.id">
                            <td>{{ item.purchasebillno || item.purchaseid || "-" }}</td>
                            <td>{{ item.itemname || item.itemid }}</td>
                            <td>{{ formatNumber(item.returnqty) }}</td>
                            <td>Rs. {{ formatNumber(item.purchaseprice) }}</td>
                            <td>{{ item.taxtype || "GST" }}<span v-if="item.istaxinclusive" class="text-muted"> (Incl)</span></td>
                            <td>{{ formatNumber(item.taxpercent) }}</td>
                            <td>Rs. {{ formatNumber(item.taxablevalue) }}</td>
                            <td>Rs. {{ formatNumber(item.cgst) }}</td>
                            <td>Rs. {{ formatNumber(item.sgst) }}</td>
                            <td>Rs. {{ formatNumber(item.igst) }}</td>
                            <td>Rs. {{ formatNumber(item.taxamount) }}</td>
                            <td>Rs. {{ formatNumber(rowTotal(item)) }}</td>
                          </tr>
                        </tbody>
                        <tfoot>
                          <tr class="detail-sum-row">
                            <td colspan="11" class="text-end"><strong>Taxable Value</strong></td>
                            <td class="text-end"><strong>Rs. {{ formatNumber(sumField(returnDetails[record.returnid].items, "taxablevalue")) }}</strong></td>
                          </tr>
                          <tr class="detail-sum-row">
                            <td colspan="11" class="text-end"><strong>CGST</strong></td>
                            <td class="text-end"><strong>Rs. {{ formatNumber(sumField(returnDetails[record.returnid].items, "cgst")) }}</strong></td>
                          </tr>
                          <tr class="detail-sum-row">
                            <td colspan="11" class="text-end"><strong>SGST</strong></td>
                            <td class="text-end"><strong>Rs. {{ formatNumber(sumField(returnDetails[record.returnid].items, "sgst")) }}</strong></td>
                          </tr>
                          <tr class="detail-sum-row">
                            <td colspan="11" class="text-end"><strong>IGST</strong></td>
                            <td class="text-end"><strong>Rs. {{ formatNumber(sumField(returnDetails[record.returnid].items, "igst")) }}</strong></td>
                          </tr>
                          <tr class="detail-sum-row">
                            <td colspan="11" class="text-end"><strong>Tax Amount</strong></td>
                            <td class="text-end"><strong>Rs. {{ formatNumber(sumField(returnDetails[record.returnid].items, "taxamount")) }}</strong></td>
                          </tr>
                          <tr class="detail-sum-row detail-sum-row-net">
                            <td colspan="11" class="text-end"><strong>Net Amount</strong></td>
                            <td class="text-end"><strong>Rs. {{ formatNumber(returnDetails[record.returnid].netamount) }}</strong></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    <div v-else class="expanded-loading">No return details found.</div>
                  </div>
                </template>
              </a-table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { buildBillPrintHtml, printBillHtml } from "../../../assets/utils/billPrint";
import { getCompanySettingsCached } from "@/services/companySettings";

axios.defaults.baseURL = process.env.VUE_APP_API_URL;
axios.defaults.headers.post["Content-Type"] = "application/json;charset=utf-8";
axios.defaults.headers.common["api-key"] = process.env.VUE_APP_API_KEY;

const columns = [
  { title: "Return Bill", dataIndex: "billno", key: "billno" },
  { title: "Supplier", dataIndex: "vendor", key: "vendor" },
  { title: "Return Date", dataIndex: "returndate", key: "returndate" },
  { title: "Status", dataIndex: "status", key: "status" },
  { title: "Items", dataIndex: "itemcount", key: "itemcount" },
  { title: "Return Qty", dataIndex: "totalqty", key: "totalqty" },
  { title: "Net Amount", dataIndex: "netamount", key: "netamount" },
  { title: "Action", key: "action", width: 220 },
];

export default {
  name: "purchasereturnlist",
  data() {
    return {
      title: "Purchase Return List",
      title1: "Manage draft, confirmed, and cancelled purchase returns",
      items: [{ text: "Add Purchase Return", addname: "/createpurchasereturn" }],
      columns,
      data: [],
      loading: false,
      search: "",
      statusFilter: "",
      filterFrom: "",
      filterTo: "",
      searchTimer: null,
      expandedRowKeys: [],
      returnDetails: {},
      detailLoading: {},
      companySettings: null,
      pagination: {
        current: 1,
        pageSize: 10,
        total: 0,
      },
    };
  },
  computed: {
    paginationConfig() {
      return {
        current: this.pagination.current,
        pageSize: this.pagination.pageSize,
        total: this.pagination.total,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} returns`,
      };
    },
  },
  beforeMount() {
    this.getReturns();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    getCurrentUserId() {
      const attrs = this.$store?.getters?.getUserDetails;
      if (!Array.isArray(attrs)) return null;
      const sub = attrs.find((attr) => attr.Name === "sub");
      return sub?.Value || null;
    },
    formatDate(value) {
      if (!value) return "-";
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    sumField(items, field) {
      return (items || []).reduce((sum, item) => sum + Number(item?.[field] || 0), 0);
    },
    rowTotal(item) {
      const amount = Number(item.amount || 0);
      const tax = Number(item.taxamount || 0);
      return item.istaxinclusive ? amount : (amount + tax);
    },
    statusClass(status) {
      return {
        "pill-draft": status === "DRAFT",
        "pill-confirmed": status === "CONFIRMED",
        "pill-cancelled": status === "CANCELLED",
      };
    },
    searching() {
      this.pagination.current = 1;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.getReturns(), 300);
    },
    applyFilters() {
      this.pagination.current = 1;
      this.getReturns();
    },
    clearFilters() {
      this.search = "";
      this.statusFilter = "";
      this.filterFrom = "";
      this.filterTo = "";
      this.pagination.current = 1;
      this.getReturns();
    },
    async getReturns() {
      this.loading = true;
      try {
        const response = await axios.get("/v1/purchase-returns", {
          params: {
            page: this.pagination.current,
            page_size: this.pagination.pageSize,
            search: this.search.trim() || undefined,
            status: this.statusFilter || undefined,
            date_from: this.filterFrom || undefined,
            date_to: this.filterTo || undefined,
          },
        });
        this.data = response.data.items || [];
        this.pagination = {
          current: response.data.page || this.pagination.current,
          pageSize: response.data.page_size || this.pagination.pageSize,
          total: response.data.total || 0,
        };
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load purchase returns." });
      } finally {
        this.loading = false;
      }
    },
    async fetchReturnDetail(returnid) {
      if (this.returnDetails[returnid] || this.detailLoading[returnid]) return;
      this.detailLoading = { ...this.detailLoading, [returnid]: true };
      try {
        const response = await axios.get(`/v1/purchase-returns/${returnid}`);
        this.returnDetails = { ...this.returnDetails, [returnid]: response.data };
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load return details." });
      } finally {
        this.detailLoading = { ...this.detailLoading, [returnid]: false };
      }
    },
    handleExpand(expanded, record) {
      if (expanded) {
        this.expandedRowKeys = [...new Set([...this.expandedRowKeys, record.returnid])];
        this.fetchReturnDetail(record.returnid);
      } else {
        this.expandedRowKeys = this.expandedRowKeys.filter((id) => id !== record.returnid);
      }
    },
    handleTableChange(pagination) {
      this.pagination.current = pagination.current;
      this.pagination.pageSize = pagination.pageSize;
      this.getReturns();
    },
    openReturn(returnid) {
      this.$router.push({ path: "/createpurchasereturn", query: { id: returnid } });
    },
    async fetchCompanySettings() {
      if (this.companySettings) return;
      try {
        this.companySettings = await getCompanySettingsCached();
      } catch (error) {
        console.error(error);
        this.companySettings = {};
      }
    },
    async printReturn(record) {
      const returnid = record?.returnid;
      if (!returnid) return;
      await this.fetchReturnDetail(returnid);
      await this.fetchCompanySettings();
      const detail = this.returnDetails[returnid];
      if (!detail) {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to load return for printing." });
        return;
      }
      const safeDate = (val) => {
        const out = this.formatDate(val);
        return out && out !== "-" ? out : "-";
      };
      const billNo = detail.billno || record.billno || "";
      const items = detail.items || [];
      const html = buildBillPrintHtml({
        documentTitle: `Purchase Return ${billNo}`,
        title: "Purchase Return Note",
        company: this.companySettings,
        partyLabel: "Supplier",
        partyRows: [
          { label: "Supplier", value: detail.vendorname || record.vendor || "-" },
          { label: "Reason",   value: detail.reason || "-" },
        ],
        billRows: [
          { label: "Return Bill",  value: billNo },
          { label: "Return Date",  value: safeDate(detail.returndate || record.returndate) },
          { label: "Status",       value: detail.status || record.status || "-" },
        ],
        columns: [
          { label: "#",         style: "width:26px;" },
          { label: "Item",      className: "item-col" },
          { label: "Qty",       className: "r", style: "width:44px;" },
          { label: "Price",     className: "r", style: "width:70px;" },
          { label: "Taxable",   className: "r", style: "width:70px;" },
          { label: "GST%",      className: "c", style: "width:36px;" },
          { label: "Tax",       className: "r", style: "width:66px;" },
          { label: "Ref Bill",  style: "width:82px;" },
          { label: "Total",     className: "r", style: "width:70px;" },
        ],
        rows: items.map((item, idx) => {
          const qty     = Number(item.returnqty || 0);
          const price   = Number(item.purchaseprice || 0);
          const taxable = Number(item.taxablevalue || 0);
          const taxPct  = Number(item.taxpercent || 0);
          const taxAmt  = Number(item.taxamount || 0);
          const incl    = item.istaxinclusive ? " (Incl)" : "";
          return [
            { text: idx + 1, className: "c" },
            { html: `${String(item.itemname || item.itemid || "")}<span class="sub">Batch: ${String(item.batchno || "-")}${incl}</span>`, className: "item-name-cell" },
            { text: this.formatNumber(qty), className: "r" },
            { text: `Rs. ${this.formatNumber(price)}`, className: "r" },
            { text: `Rs. ${this.formatNumber(taxable)}`, className: "r" },
            { text: String(taxPct), className: "c" },
            { text: `Rs. ${this.formatNumber(taxAmt)}`, className: "r" },
            { text: String(item.purchasebillno || item.purchaseid || "-") },
            { text: `Rs. ${this.formatNumber(this.rowTotal(item))}`, className: "r" },
          ];
        }),
        subTotalRow: [
          { text: "", className: "c" },
          { text: "Sub-Total" },
          { text: this.formatNumber(items.reduce((s, i) => s + Number(i.returnqty || 0), 0)), className: "r" },
          { text: "" },
          { text: `Rs. ${this.formatNumber(items.reduce((s, i) => s + Number(i.taxablevalue || 0), 0))}`, className: "r" },
          { text: "" },
          { text: `Rs. ${this.formatNumber(items.reduce((s, i) => s + Number(i.taxamount || 0), 0))}`, className: "r" },
          { text: "" },
          { text: `Rs. ${this.formatNumber(items.reduce((s, i) => s + this.rowTotal(i), 0))}`, className: "r" },
        ],
        totals: [
          { label: "Taxable Value", value: `Rs. ${this.formatNumber(items.reduce((s, i) => s + Number(i.taxablevalue || 0), 0))}` },
          { label: "CGST",         value: `Rs. ${this.formatNumber(items.reduce((s, i) => s + Number(i.cgst || 0), 0))}` },
          { label: "SGST",         value: `Rs. ${this.formatNumber(items.reduce((s, i) => s + Number(i.sgst || 0), 0))}` },
          { label: "Discount",     value: `- Rs. ${this.formatNumber(detail.discountamount || 0)}` },
          { label: "Tax",          value: `Rs. ${this.formatNumber(detail.taxamount || 0)}` },
          { label: "Round Off",    value: `Rs. ${this.formatNumber(detail.roundoff || 0)}` },
          { label: "Net Amount",   value: `Rs. ${this.formatNumber(detail.netamount || 0)}` },
        ],
        footerNote: this.companySettings?.InvoiceFooter || this.companySettings?.InvoiceTerms,
        itemColumnWidth: "24%",
        netAmount: detail.netamount,
      });
      printBillHtml(html, (text) => {
        Swal.fire({ icon: "error", title: "Print Error", text });
      });
    },
    async confirmReturn(returnid) {
      const result = await Swal.fire({
        title: "Confirm purchase return?",
        text: "This will reduce stock for the selected batches.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, confirm",
      });
      if (!result.isConfirmed) return;
      try {
        await axios.post(`/v1/purchase-returns/${returnid}/confirm`, { actorid: this.getCurrentUserId() });
        Swal.fire({ icon: "success", title: "Return confirmed", timer: 1300, showConfirmButton: false });
        this.returnDetails = {};
        this.getReturns();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to confirm purchase return." });
      }
    },
    async cancelReturn(returnid) {
      const result = await Swal.fire({
        title: "Cancel purchase return?",
        text: "Confirmed returns will be reversed with a new ledger entry.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, cancel it",
      });
      if (!result.isConfirmed) return;
      try {
        await axios.post(`/v1/purchase-returns/${returnid}/cancel`, { actorid: this.getCurrentUserId() });
        Swal.fire({ icon: "success", title: "Return cancelled", timer: 1300, showConfirmButton: false });
        this.returnDetails = {};
        this.getReturns();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: error.response?.data?.detail || "Failed to cancel purchase return." });
      }
    },
  },
};
</script>

<style scoped>
.table-top { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:16px; }
.search-input { display:flex; align-items:center; gap:6px; }
.search-input .form-control { width:340px; height:36px; border-radius:6px; border:1px solid #e5e7eb; padding:0 12px; }
.list-stats { display:flex; gap:10px; align-items:center; }
.stat-chip { display:flex; align-items:center; gap:5px; font-size:0.82rem; color:#6b7280; background:#f9fafb; border:1px solid #e5e7eb; border-radius:20px; padding:3px 10px; }
.filter-row { display:flex; gap:10px; align-items:end; flex-wrap:wrap; margin-bottom:16px; }
.filter-group { display:flex; flex-direction:column; gap:4px; }
.filter-label { font-size:0.75rem; color:#6b7280; font-weight:600; }
.filter-input { width:150px; height:36px; border-radius:6px; border:1px solid #e5e7eb; }
.btn-clear-filter { height:36px; padding:0 14px; border:none; border-radius:6px; background:#f3f4f6; color:#374151; font-size:0.85rem; font-weight:600; }
.loading-wrap, .empty-wrap { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:50px 20px; gap:10px; }
.loading-spinner { width:36px; height:36px; border:3px solid #f3f4f6; border-top-color:#fb923c; border-radius:50%; animation:spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.empty-title { font-size:1rem; font-weight:700; color:#111827; margin:0; }
.empty-sub { font-size:0.84rem; color:#6b7280; margin:0; }
.bill-cell { display:flex; flex-direction:column; gap:4px; }
.bill-badge { display:inline-block; background:#fff7ed; color:#c2410c; border:1px solid #fdba74; border-radius:20px; padding:2px 10px; font-size:.78rem; font-weight:700; width:max-content; }
.bill-id { color:#9ca3af; font-size:.72rem; }
.status-pill { display:inline-block; padding:2px 10px; border-radius:999px; font-size:.76rem; font-weight:700; }
.pill-draft { background:#fff7ed; color:#c2410c; }
.pill-confirmed { background:#f0fdf4; color:#15803d; }
.pill-cancelled { background:#fef2f2; color:#dc2626; }
.date-text { font-size:.82rem; color:#6b7280; }
.price-total { color:#ea580c; font-weight:700; }
.action-btns { display:flex; gap:6px; flex-wrap:wrap; }
.act-btn { border:none; border-radius:7px; padding:6px 10px; font-size:.78rem; font-weight:700; }
.act-open { background:#eff6ff; color:#2563eb; }
.act-confirm { background:#f0fdf4; color:#15803d; }
.act-cancel { background:#fef2f2; color:#dc2626; }
.act-print { background:#f0f4ff; color:#4f46e5; font-size:.82rem; }
.expanded-wrap { background:#fffaf5; border:1px solid #fed7aa; border-radius:10px; padding:14px; }
.detail-header { display:flex; gap:18px; flex-wrap:wrap; margin-bottom:10px; font-size:.82rem; color:#6b7280; }
.detail-table { width:100%; border-collapse:collapse; }
.detail-table th, .detail-table td { padding:8px 10px; border-bottom:1px solid #f3e8d8; font-size:.82rem; text-align:left; }
.detail-table th { color:#6b7280; font-weight:700; text-transform:uppercase; font-size:.72rem; }
.detail-table tfoot td { border-bottom:none; border-top:1px solid #f3e8d8; }
.detail-sum-row td { background:#fff7f0; }
.detail-sum-row-net td { background:#ffedd5; }
.expanded-loading { color:#6b7280; font-size:.82rem; }
</style>
